import { Container, Graphics } from 'pixi.js';
import { biomePrim } from './stars';
import { rnd } from './util';

const SCROLL = 90;

// Lasers are angled rays anchored on top OR bottom wall, extending across the screen.
// They spawn sporadically (frequency scales with wave) and run a single lifecycle:
//   warn (telegraph) → wipe (sweep along ray) → active (deadly) → fade (blink + die).
// Multiple lasers can coexist but the spawner rate-limits to keep the playfield clear.
interface Laser {
  wall: 'top' | 'bot';
  sourceX: number;       // wall-anchor x
  angle: number;         // radians; 0 = perpendicular into playfield (current)
  startAngle: number;    // angle at spawn (used for warn-phase render)
  swipeRate: number;     // radians per second during active phase
  t: number;             // frames into lifecycle
  warnDur: number;
  wipeDur: number;
  activeDur: number;
  fadeDur: number;
}

interface Pulse {
  wall: 'top' | 'bot';
  x: number;
  depth: number;
  t: number;
  dur: number;
}

export class Terrain {
  private gfx: Graphics;
  private spikeGfx: Graphics;
  off = 0;
  spikes: Laser[] = [];
  pulses: Pulse[] = [];
  pulseTimer = 0;
  private spawnT = 0;

  constructor(stage: Container) {
    this.gfx = new Graphics();
    stage.addChild(this.gfx);
    this.spikeGfx = new Graphics();
    stage.addChild(this.spikeGfx);
  }

  topY(x: number, W: number, _H: number): number {
    const k = (x + this.off) / W;
    const a = Math.sin(k * 7) * 18;
    const b = Math.sin(k * 13 + 1.7) * 12;
    let pulse = 0;
    for (const p of this.pulses) {
      if (p.wall !== 'top') continue;
      const dx = x - p.x;
      if (Math.abs(dx) < 240) {
        const fy = Math.cos((dx / 240) * (Math.PI / 2));
        const ft = Math.sin(Math.PI * Math.max(0, Math.min(1, p.t / p.dur)));
        pulse += p.depth * fy * ft;
      }
    }
    return 30 + a + b + pulse;
  }
  botY(x: number, W: number, H: number): number {
    const k = (x + this.off) / W;
    const a = Math.sin(k * 6 + 0.5) * 18;
    const b = Math.sin(k * 11 + 2.3) * 14;
    let pulse = 0;
    for (const p of this.pulses) {
      if (p.wall !== 'bot') continue;
      const dx = x - p.x;
      if (Math.abs(dx) < 240) {
        const fy = Math.cos((dx / 240) * (Math.PI / 2));
        const ft = Math.sin(Math.PI * Math.max(0, Math.min(1, p.t / p.dur)));
        pulse -= p.depth * fy * ft;
      }
    }
    return H - 30 + a + b + pulse;
  }

  // Spawn a single laser at random wall + x + angle.
  private spawnLaser(): void {
    const W = window.innerWidth;
    const wall: 'top' | 'bot' = Math.random() < 0.5 ? 'top' : 'bot';
    const sourceX = rnd(W * 0.15, W * 0.85);
    // Initial angle ±20° from perpendicular
    const startAngle = rnd(-Math.PI / 9, Math.PI / 9);
    // Swipe rate ±0.3..0.55 rad/s, random direction
    const swipeRate = (Math.random() < 0.5 ? -1 : 1) * rnd(0.30, 0.55);
    this.spikes.push({
      wall, sourceX, angle: startAngle, startAngle, swipeRate, t: 0,
      warnDur: 55,    // ~0.92s telegraph
      wipeDur: 26,    // ~0.43s sweep along ray
      activeDur: 165, // ~2.75s deadly
      fadeDur: 36,    // ~0.60s blink-fade
    });
  }

  update(dt: number, wave = 1): void {
    this.off += SCROLL * dt;
    // Spawn rate scales with wave: every (8 - wave*0.25)s, clamped 3..8s. Cap concurrent lasers.
    this.spawnT += dt;
    const interval = Math.max(3.0, 8 - wave * 0.25);
    if (this.spikes.length < 2 && this.spawnT > interval) {
      this.spawnT = 0;
      this.spawnLaser();
    }
    // Tick each laser; remove finished ones; swipe angle during wipe/active/fade
    for (let i = this.spikes.length - 1; i >= 0; i--) {
      const s = this.spikes[i];
      s.t += dt * 60;
      // Sweep the angle during firing phases (after warn). Clamp to ±50° from perpendicular.
      if (s.t > s.warnDur) {
        s.angle += s.swipeRate * dt;
        const clamp = Math.PI / 3.6;
        if (s.angle > clamp) { s.angle = clamp; s.swipeRate = -Math.abs(s.swipeRate); }
        if (s.angle < -clamp) { s.angle = -clamp; s.swipeRate = Math.abs(s.swipeRate); }
      }
      const total = s.warnDur + s.wipeDur + s.activeDur + s.fadeDur;
      if (s.t >= total) this.spikes.splice(i, 1);
    }
    // Pulse system (terrain wave-deformation visual)
    this.pulseTimer += dt;
    if (this.pulses.length === 0 && this.pulseTimer > 10) {
      this.pulseTimer = 0;
      this.pulses.push({
        wall: Math.random() < 0.5 ? 'top' : 'bot',
        x: rnd(window.innerWidth * 0.2, window.innerWidth * 0.8),
        depth: rnd(40, 80),
        t: 0, dur: 3,
      });
    }
    for (let i = this.pulses.length - 1; i >= 0; i--) {
      this.pulses[i].t += dt;
      if (this.pulses[i].t >= this.pulses[i].dur) this.pulses.splice(i, 1);
    }
  }

  // Phase machine: warn → wipe → active → fade.
  private laserPhase(s: Laser): { phase: 'warn' | 'wipe' | 'active' | 'fade'; frac: number } {
    let t = s.t;
    if (t < s.warnDur) return { phase: 'warn', frac: t / s.warnDur };
    t -= s.warnDur;
    if (t < s.wipeDur) return { phase: 'wipe', frac: t / s.wipeDur };
    t -= s.wipeDur;
    if (t < s.activeDur) return { phase: 'active', frac: t / s.activeDur };
    t -= s.activeDur;
    return { phase: 'fade', frac: Math.min(1, t / s.fadeDur) };
  }

  // Compute (sx, sy) source point and (ex, ey) end point clipped to screen bounds.
  private rayEndpoints(s: Laser, W: number, H: number): { sx: number; sy: number; ex: number; ey: number } {
    const sx = s.sourceX;
    const sy = s.wall === 'top' ? this.topY(sx, W, H) : this.botY(sx, W, H);
    // Direction: top wall fires DOWN (+y), bot wall fires UP (-y); angle 0 = straight perpendicular
    const baseDir = s.wall === 'top' ? Math.PI / 2 : -Math.PI / 2;
    const dx = Math.cos(baseDir + s.angle);
    const dy = Math.sin(baseDir + s.angle);
    // Extend until the ray exits the screen — find nearest positive-t edge intersection
    let tMax = Infinity;
    if (dx > 0.001) tMax = Math.min(tMax, (W + 40 - sx) / dx);
    else if (dx < -0.001) tMax = Math.min(tMax, (-40 - sx) / dx);
    if (dy > 0.001) tMax = Math.min(tMax, (H + 40 - sy) / dy);
    else if (dy < -0.001) tMax = Math.min(tMax, (-40 - sy) / dy);
    return { sx, sy, ex: sx + dx * tMax, ey: sy + dy * tMax };
  }

  draw(W: number, H: number, wave: number): void {
    const g = this.gfx;
    g.clear();
    const col = biomePrim(wave);
    g.moveTo(0, 0);
    for (let x = 0; x <= W; x += 18) g.lineTo(x, this.topY(x, W, H));
    g.lineTo(W, 0);
    g.fill({ color: col, alpha: 0.18 });
    g.stroke({ width: 2, color: col, alpha: 0.7 });

    g.moveTo(0, H);
    for (let x = 0; x <= W; x += 18) g.lineTo(x, this.botY(x, W, H));
    g.lineTo(W, H);
    g.fill({ color: col, alpha: 0.18 });
    g.stroke({ width: 2, color: col, alpha: 0.7 });

    // Lasers
    const sg = this.spikeGfx;
    sg.clear();
    for (const s of this.spikes) {
      const { phase, frac } = this.laserPhase(s);
      const { sx, sy, ex, ey } = this.rayEndpoints(s, W, H);
      const dirX = ex - sx, dirY = ey - sy;

      if (phase === 'warn') {
        // Amber telegraph at the source + dim guide line along the full ray
        const a = 0.3 + 0.55 * frac;
        // Source triangle pointing into playfield
        const perpX = -dirY / Math.hypot(dirX, dirY);
        const perpY = dirX / Math.hypot(dirX, dirY);
        const tipDist = 14;
        const px1 = sx + perpX * 9, py1 = sy + perpY * 9;
        const px2 = sx - perpX * 9, py2 = sy - perpY * 9;
        const tipX = sx + (dirX / Math.hypot(dirX, dirY)) * tipDist;
        const tipY = sy + (dirY / Math.hypot(dirX, dirY)) * tipDist;
        sg.poly([px1, py1, px2, py2, tipX, tipY]);
        sg.fill({ color: 0xff8800, alpha: a });
        // Faint dashed guide line (single thin line along full ray)
        sg.moveTo(sx, sy);
        sg.lineTo(ex, ey);
        sg.stroke({ width: 1, color: 0xffaa44, alpha: 0.2 + 0.5 * frac });
        continue;
      }

      // Beam alpha + extension
      let extent = 1;
      let alphaMul = 1;
      if (phase === 'wipe') {
        extent = 1 - Math.pow(1 - frac, 2);
        alphaMul = 0.7 + 0.3 * frac;
      } else if (phase === 'fade') {
        const blink = Math.abs(Math.sin(frac * Math.PI * 7));
        alphaMul = (1 - frac) * (0.4 + 0.6 * blink);
      }

      const beamEndX = sx + dirX * extent;
      const beamEndY = sy + dirY * extent;
      const len = Math.hypot(dirX * extent, dirY * extent);
      if (len < 1) continue;

      // Build a thick-line polygon (4-vertex quad) along the ray for halo / mid / core
      const ang = Math.atan2(beamEndY - sy, beamEndX - sx);
      const px = -Math.sin(ang), py = Math.cos(ang); // perpendicular unit vector
      const drawBeam = (halfW: number, color: number, alpha: number): void => {
        sg.poly([
          sx + px * halfW, sy + py * halfW,
          sx - px * halfW, sy - py * halfW,
          beamEndX - px * halfW, beamEndY - py * halfW,
          beamEndX + px * halfW, beamEndY + py * halfW,
        ]);
        sg.fill({ color, alpha: alpha * alphaMul });
      };
      drawBeam(6, 0xff44ff, 0.18);
      drawBeam(3, 0xff44ff, 0.55);
      drawBeam(1, 0xffffff, 0.95);

      // Source anchor — bright triangle pointing along ray
      const tipDist = 16;
      const tipX = sx + Math.cos(ang) * tipDist;
      const tipY = sy + Math.sin(ang) * tipDist;
      sg.poly([
        sx + px * 11, sy + py * 11,
        sx - px * 11, sy - py * 11,
        tipX, tipY,
      ]);
      sg.fill({ color: 0xff44ff, alpha: 0.9 * alphaMul });
    }
  }

  // Collision: distance from player center to the active beam segment.
  collideSpike(p: { x: number; y: number; radius: number }, W: number, H: number): boolean {
    for (const s of this.spikes) {
      const { phase, frac } = this.laserPhase(s);
      const deadly = phase === 'active' || (phase === 'wipe' && frac > 0.3);
      if (!deadly) continue;
      const { sx, sy, ex, ey } = this.rayEndpoints(s, W, H);
      const dx = ex - sx, dy = ey - sy;
      const ext = phase === 'wipe' ? 1 - Math.pow(1 - frac, 2) : 1;
      const beamEx = sx + dx * ext, beamEy = sy + dy * ext;
      // Closest-point on segment
      const segDx = beamEx - sx, segDy = beamEy - sy;
      const lenSq = segDx * segDx + segDy * segDy;
      if (lenSq < 1) continue;
      let tt = ((p.x - sx) * segDx + (p.y - sy) * segDy) / lenSq;
      tt = Math.max(0, Math.min(1, tt));
      const cx = sx + segDx * tt, cy = sy + segDy * tt;
      const distSq = (p.x - cx) * (p.x - cx) + (p.y - cy) * (p.y - cy);
      const r = 6 + p.radius;
      if (distSq < r * r) return true;
    }
    return false;
  }

  // Find a colliding deadly laser; force-fade it (treat as energy spent).
  // Returns the laser source angle for reflect direction, or null if none.
  consumeLaser(p: { x: number; y: number; radius: number }, W: number, H: number): { sourceX: number; sourceY: number; angle: number } | null {
    for (const s of this.spikes) {
      const { phase, frac } = this.laserPhase(s);
      const deadly = phase === 'active' || (phase === 'wipe' && frac > 0.3);
      if (!deadly) continue;
      const { sx, sy, ex, ey } = this.rayEndpoints(s, W, H);
      const dx = ex - sx, dy = ey - sy;
      const ext = phase === 'wipe' ? 1 - Math.pow(1 - frac, 2) : 1;
      const beamEx = sx + dx * ext, beamEy = sy + dy * ext;
      const segDx = beamEx - sx, segDy = beamEy - sy;
      const lenSq = segDx * segDx + segDy * segDy;
      if (lenSq < 1) continue;
      let tt = ((p.x - sx) * segDx + (p.y - sy) * segDy) / lenSq;
      tt = Math.max(0, Math.min(1, tt));
      const cx = sx + segDx * tt, cy = sy + segDy * tt;
      const distSq = (p.x - cx) * (p.x - cx) + (p.y - cy) * (p.y - cy);
      const r = 6 + p.radius;
      if (distSq < r * r) {
        // Skip remaining lifecycle; jump to fade so the beam dies on hit
        s.t = s.warnDur + s.wipeDur + s.activeDur + 1;
        const baseDir = s.wall === 'top' ? Math.PI / 2 : -Math.PI / 2;
        return { sourceX: sx, sourceY: sy, angle: baseDir + s.angle };
      }
    }
    return null;
  }

  // Demo-mode helper: returns a unit vector pointing AWAY from the nearest deadly laser
  // within `dangerR` radius, plus the distance. Returns null if no laser is threatening.
  laserDodgeVector(px: number, py: number, dangerR: number, W: number, H: number): { x: number; y: number; dist: number } | null {
    let best: { x: number; y: number; dist: number } | null = null;
    for (const s of this.spikes) {
      const { phase, frac } = this.laserPhase(s);
      const deadly = phase === 'active' || phase === 'wipe';
      if (!deadly) continue;
      const { sx, sy, ex, ey } = this.rayEndpoints(s, W, H);
      const dx = ex - sx, dy = ey - sy;
      const ext = phase === 'wipe' ? 1 - Math.pow(1 - frac, 2) : 1;
      const beamEx = sx + dx * ext, beamEy = sy + dy * ext;
      const segDx = beamEx - sx, segDy = beamEy - sy;
      const lenSq = segDx * segDx + segDy * segDy;
      if (lenSq < 1) continue;
      let tt = ((px - sx) * segDx + (py - sy) * segDy) / lenSq;
      tt = Math.max(0, Math.min(1, tt));
      const cx = sx + segDx * tt, cy = sy + segDy * tt;
      const ddx = px - cx, ddy = py - cy;
      const d = Math.hypot(ddx, ddy);
      if (d > dangerR) continue;
      if (!best || d < best.dist) {
        const nrm = d > 0.001 ? d : 1;
        best = { x: ddx / nrm, y: ddy / nrm, dist: d };
      }
    }
    return best;
  }

  collidePlayer(p: { x: number; y: number; vy: number; radius: number; hp: number; invuln: number; shield: number; maxShield: number }, W: number, H: number): boolean {
    if (p.invuln > 0) return false;
    const top = this.topY(p.x, W, H);
    const bot = this.botY(p.x, W, H);
    if (p.y < top + p.radius) {
      p.y = top + p.radius;
      p.vy = Math.abs(p.vy) * 0.4;
      return true;
    }
    if (p.y > bot - p.radius) {
      p.y = bot - p.radius;
      p.vy = -Math.abs(p.vy) * 0.4;
      return true;
    }
    return false;
  }
}
